import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import { appThemes, AppThemeName, resolveInitialTheme, THEME_STORAGE_KEY } from "./themes";

interface ThemeContextValue {
  themeName: AppThemeName;
  setThemeName: (name: AppThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeNameState] = useState<AppThemeName>(resolveInitialTheme);

  const setThemeName = useCallback((name: AppThemeName) => {
    setThemeNameState(name);
    localStorage.setItem(THEME_STORAGE_KEY, name);
    document.documentElement.setAttribute("data-theme", name);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeName(themeName === "light" ? "dark" : "light");
  }, [setThemeName, themeName]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeName);
  }, [themeName]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeName,
      setThemeName,
      toggleTheme,
    }),
    [themeName, setThemeName, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={appThemes[themeName]}>
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useAppTheme must be used within AppThemeProvider");
  }
  return ctx;
}
